<template>
  <PublicLayout page-title="Precios">
    <div class="pricing-page">
    <!-- ===================== -->
    <!-- HERO SECTION          -->
    <!-- ===================== -->
    <section class="bg-organic relative py-24 overflow-hidden">
      <!-- Background decorations -->
      <div class="absolute top-0 right-0 w-96 h-96 bg-accent-200/20 rounded-full blur-3xl" />
      <div class="absolute bottom-0 left-0 w-80 h-80 bg-teal-200/20 rounded-full blur-3xl" />

      <div class="relative max-w-4xl mx-auto px-6 text-center z-10">
        <!-- Badge -->
        <div class="inline-flex items-center gap-2 px-4 py-2 bg-accent-50 border border-accent-200
                    rounded-full text-accent-700 text-sm font-medium mb-6 animate-fade-up">
          <SparklesIcon class="w-4 h-4" />
          <span>Precios transparentes, sin sorpresas</span>
        </div>

        <!-- Titulo -->
        <h1 class="text-5xl md:text-6xl lg:text-7xl font-bold text-text-primary
                   tracking-tight mb-6 animate-fade-up delay-100">
          Elige el servicio
          <span class="bg-gradient-to-r from-accent-600 to-teal-500 bg-clip-text text-transparent">
            que necesitas
          </span>
        </h1>

        <!-- Subtitulo -->
        <p class="text-xl md:text-2xl text-text-secondary max-w-2xl mx-auto mb-8
                  animate-fade-up delay-200">
          Un solo pago, protección completa. Sin suscripciones, sin compromisos mensuales.
        </p>

        <!-- Current Plan Banner (authenticated users) -->
        <div v-if="isAuthenticated" class="inline-flex items-center gap-3 bg-white
                    rounded-full px-6 py-3 shadow-lg border border-slate-200 mb-6 animate-fade-up delay-300">
          <span class="text-sm text-text-secondary">Plan actual:</span>
          <TierBadge
            :tier="userTier"
            size="sm"
            variant="gradient"
            :show-plan="true"
          />
        </div>

        <!-- Trust indicators inline -->
        <div class="flex flex-wrap items-center justify-center gap-6 text-sm text-text-secondary
                    animate-fade-up delay-300">
          <div class="flex items-center gap-2">
            <ShieldCheckIcon class="w-5 h-5 text-accent-500" />
            <span>GDPR Compliant</span>
          </div>
          <div class="flex items-center gap-2">
            <LockClosedIcon class="w-5 h-5 text-accent-500" />
            <span>Pago seguro con Stripe</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ===================== -->
    <!-- PRICING CARDS         -->
    <!-- ===================== -->
    <section id="pricing-cards" class="py-24 px-6">
      <ServicePricingCards
        :show-description="true"
        :features-limit="5"
        cta-text="Comenzar"
        :show-guarantee="false"
        :compact="true"
        :is-authenticated="isAuthenticated"
        :user-tier="userTier"
        :loading="loading"
        :selected-tier="selectedPlan"
        @select-plan="selectPlan"
      />
    </section>


    <!-- ===================== -->
    <!-- TRUST SECTION         -->
    <!-- ===================== -->
    <section class="py-24">
      <div class="max-w-6xl mx-auto px-6">
        <!-- Garantias -->
        <div class="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto animate-fade-up delay-100">
          <div class="bg-white rounded-2xl p-6 border border-slate-200 text-center
                      hover:shadow-lg transition-shadow">
            <LockClosedIcon class="w-12 h-12 text-accent-500 mx-auto mb-4" />
            <h3 class="font-bold text-text-primary mb-2">Pago 100% seguro</h3>
            <p class="text-sm text-text-secondary">
              Procesamos pagos con Stripe, el estándar de seguridad en pagos online.
            </p>
          </div>
          <div class="bg-white rounded-2xl p-6 border border-slate-200 text-center
                      hover:shadow-lg transition-shadow">
            <CheckBadgeIcon class="w-12 h-12 text-accent-500 mx-auto mb-4" />
            <h3 class="font-bold text-text-primary mb-2">GDPR Compliant</h3>
            <p class="text-sm text-text-secondary">
              Todos tus datos están protegidos conforme al Reglamento Europeo de Protección de Datos.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- ===================== -->
    <!-- FAQ SECTION           -->
    <!-- ===================== -->
    <section class="py-24 bg-slate-50/50">
      <div class="max-w-3xl mx-auto px-6">
        <div class="text-center mb-12 animate-fade-up">
          <span class="text-sm font-semibold text-accent-600 uppercase tracking-widest mb-4 block">FAQ</span>
          <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-4">
            Preguntas sobre pagos
          </h2>
          <p class="text-text-secondary">
            Resolvemos tus dudas antes de empezar
          </p>
        </div>

        <div class="space-y-4 animate-fade-up delay-100">
          <FAQItem
            v-for="(faq, index) in faqs"
            :key="index"
            :question="faq.question"
            :answer="faq.answer"
            :open="faq.open"
          />
        </div>
      </div>
    </section>

    <!-- ===================== -->
    <!-- CTA SECTION           -->
    <!-- ===================== -->
    <section class="py-24 bg-gradient-to-br from-accent-600 to-teal-600 relative overflow-hidden">
      <!-- Decoracion -->
      <div class="absolute inset-0 opacity-10">
        <div class="absolute top-0 left-1/4 w-64 h-64 bg-white rounded-full blur-3xl" />
        <div class="absolute bottom-0 right-1/4 w-80 h-80 bg-white rounded-full blur-3xl" />
      </div>

      <div class="relative max-w-3xl mx-auto px-6 text-center">
        <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 animate-fade-up">
          Protege tu privacidad digital hoy
        </h2>
        <p class="text-xl text-white/80 mb-8 animate-fade-up delay-100">
          Expertos en GDPR y derecho al olvido a tu servicio
        </p>
        <div class="flex justify-center animate-fade-up delay-200">
          <a
            href="#pricing-cards"
            @click.prevent="scrollToCards"
            class="px-8 py-4 bg-white text-accent-600 font-bold rounded-2xl
                   hover:bg-white/90 transition-all shadow-lg
                   hover:shadow-xl hover:scale-105 cursor-pointer"
          >
            Ver nuestros servicios
          </a>
        </div>
      </div>
    </section>
    </div>
  </PublicLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useSeo } from '@/composables/useSeo'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import {
  SparklesIcon,
  ShieldCheckIcon,
  LockClosedIcon,
  CheckBadgeIcon
} from '@heroicons/vue/24/outline'
import TierBadge from '@/components/dashboard/TierBadge.vue'
import FAQItem from '@/components/ui/FAQItem.vue'
import PublicLayout from '@/components/layout/PublicLayout.vue'
import ServicePricingCards from '@/components/pricing/ServicePricingCards.vue'

/**
 * Pricing Page V3 - Independent Services Design
 *
 * Applying V3 design system from DESIGN_SYSTEM_V2.md
 * Services are presented as INDEPENDENT products, not tiers
 *
 * Features:
 * - Hero section with organic blobs and trust indicators
 * - Service pricing cards (independent products)
 * - Trust stats and guarantees section
 * - FAQ with accordions
 * - Gradient CTA section
 * - Preserved authentication logic for upgrades
 */

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const { user } = storeToRefs(authStore)

// SEO Meta Tags
useSeo({
  title: 'Precios - Servicios de Protección Digital',
  description: 'Tarifas: Derecho al Olvido 499€, Post-Mortem 699€, Testamento Digital 599€. Pago único, sin costes ocultos.',
  canonical: '/pricing'
})

// State
const loading = ref(false)
const selectedPlan = ref(null)

// Computed
const isAuthenticated = computed(() => !!user.value)
const userTier = computed(() => user.value?.subscriptionTier || 'olvido')

// FAQ data
const faqs = [
  {
    question: '¿Qué servicio necesito?',
    answer: 'Ofrecemos tres servicios independientes según tu situación: <strong>Derecho al Olvido (499 euros)</strong> si quieres eliminar tu información personal de internet, <strong>Derecho al Olvido Post-Mortem (699 euros)</strong> si eres familiar y necesitas gestionar los datos de una persona fallecida, o <strong>Testamento Digital (599 euros)</strong> si quieres planificar tu legado digital en vida.',
    open: true
  },
  {
    question: '¿Los pagos son únicos o hay suscripción?',
    answer: 'Todos nuestros servicios son de <strong>pago único</strong>. No hay suscripciones mensuales, ni pagos recurrentes, ni costes ocultos. Pagas una vez y gestionamos tu caso hasta su resolución.',
    open: false
  },
  {
    question: '¿Puedo contratar varios servicios?',
    answer: 'Sí, cada servicio es independiente y puedes contratarlos según tus necesidades. No hay vinculación entre ellos ni es necesario contratar uno para acceder a otro.',
    open: false
  },
  {
    question: '¿Hay garantía de devolución?',
    answer: 'Sí. Si tras el análisis previo de viabilidad consideramos que tu caso no reúne los requisitos legales necesarios para tramitar la solicitud conforme al artículo 17 del RGPD, te devolvemos el 100% del importe.',
    open: false
  },
  {
    question: '¿Qué métodos de pago aceptáis?',
    answer: 'Aceptamos todas las tarjetas de crédito y débito principales (Visa, Mastercard, American Express) a través de Stripe. El pago es completamente seguro y cifrado.',
    open: false
  }
]

// Methods
const selectPlan = async (plan) => {
  selectedPlan.value = plan
  loading.value = true

  try {
    if (!isAuthenticated.value) {
      router.push({
        name: 'Register',
        query: { plan }
      })
    } else {
      // Handle service purchase for existing user
      // TODO: Implement payment service integration
      setTimeout(() => {
        router.push({
          name: 'PaymentSuccess',
          query: { plan }
        })
      }, 2000)
    }
  } catch (error) {
    console.error('Error selecting plan:', error)
  } finally {
    loading.value = false
    selectedPlan.value = null
  }
}

const scrollToCards = () => {
  const element = document.querySelector('#pricing-cards')
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' })
  }
}

</script>

<style scoped>
@reference "@/assets/styles/main.css";

.pricing-page {
  min-height: 100vh;
  background-color: #f9fafb;
}

/* Smooth scrolling */
html {
  scroll-behavior: smooth;
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .pricing-page *,
  .pricing-page *::before,
  .pricing-page *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}
</style>
